import { Logger } from '_common/services';
import { Status } from '../../models';
import BaseController from '../BaseController';
import { TaskList } from './TaskList';
import { IndexerDeltaType, ModelIndexer } from '../Models/ModelIndexer';
import { v4 as uuid } from 'uuid';
import { reject } from 'lodash-es';
import { RealtimeResponse } from '_common/services/Realtime/Transport';
import { ModelsController } from '../Models/ModelsController';
import { RichTextParser } from '../../common';

export class DeletedTaskList extends ModelIndexer<'TASK'> {
  start(documentId: string) {
    super.start({
      p_id: documentId,
      't.dlt': { $exists: true },
    });
  }
}
export class TasksController extends BaseController {
  private tasksList?: TaskList;
  private deletedTasksList?: DeletedTaskList;

  constructor(Data: Editor.Data.State) {
    super(Data);
    this.handleTaskListError = this.handleTaskListError.bind(this);
    this.handleTaskListChanged = this.handleTaskListChanged.bind(this);
    this.handleTaskChanged = this.handleTaskChanged.bind(this);
    this.handleDeletedTaskListChanged = this.handleDeletedTaskListChanged.bind(this);
    this.handleDeletedTaskListInserted = this.handleDeletedTaskListInserted.bind(this);
    this.handleDeletedTaskListRemoved = this.handleDeletedTaskListRemoved.bind(this);
  }

  start(documentId: string): void {
    this.tasksList = this.Data.models?.getIndexer('TASK') as TaskList;
    this.tasksList.on('ERROR', this.handleTaskListError);
    this.tasksList.on('CHANGED_DELTA', this.handleTaskListChanged);
    this.deletedTasksList = new DeletedTaskList(
      this.Data.transport,
      this.Data.models as ModelsController,
      'TASK',
    );
    this.deletedTasksList.on('INSERTED', this.handleDeletedTaskListInserted);
    this.deletedTasksList.on('REMOVED', this.handleDeletedTaskListRemoved);
    this.deletedTasksList.on('CHANGED', this.handleDeletedTaskListChanged);
    this.tasksList.start(documentId);
    this.deletedTasksList.start(documentId);
  }

  private handleTaskListError(error: Error) {
    Logger.error(error);
  }

  private async handleTaskListChanged(data: IndexerDeltaType<string[]>) {
    const jobs: Promise<void>[] = [];

    for (let index = 0; index < data.in.length; index++) {
      const element = this.tasksList?.task(data.in[index]);

      if (element && !element.loaded) {
        jobs.push(
          element.awaitForEvent('LOADED').then(() => {
            element.on('LOADED', this.handleTaskChanged);
            element.on('UPDATED', this.handleTaskChanged);
            return;
          }),
        );
      } else {
        element?.on('LOADED', this.handleTaskChanged);
        element?.on('UPDATED', this.handleTaskChanged);
      }
    }
    await Promise.all(jobs);
    this.loadTasksData();
  }

  private handleDeletedTaskListInserted(inserted: Editor.Data.Task[]) {
    for (let index = 0; index < inserted.length; index++) {
      const element = inserted[index];
      element?.on('LOADED', this.handleTaskChanged);
      element?.on('UPDATED', this.handleTaskChanged);
    }
  }

  private handleDeletedTaskListRemoved(removed: Editor.Data.Task[]) {
    for (let index = 0; index < removed.length; index++) {
      const element = removed[index];
      element?.off('LOADED', this.handleTaskChanged);
      element?.off('UPDATED', this.handleTaskChanged);
    }
  }

  private handleDeletedTaskListChanged(data: Editor.Data.Task[]) {
    this.loadDeletedTasksData();
  }

  private loadDeletedTasksData() {
    const tasksData: { [index: string]: Editor.Data.Tasks.TaskData } = {};
    const tasksList: string[] = [];
    const dataObjs = this.deletedTasksList?.results || [];

    for (let index = 0; index < dataObjs.length; index++) {
      const task = dataObjs[index];
      tasksList.push(task.id);
      tasksData[task.id] = {
        id: task.id,
        ...(task.get([]) || {}),
      };
    }
    this.Data.events?.emit(
      'LOAD_DELETED_TASKS_DATA',
      tasksList,
      RichTextParser.parseTasks(tasksData),
    );
  }

  private loadTasksData() {
    let tasksData: Editor.Data.Tasks.TasksData = {};
    const tasksList = this.tasksList?.list || [];

    for (let index = 0; index < tasksList.length; index++) {
      const taskId: string = tasksList[index];

      const task = this.tasksList?.task(taskId).get([]) || {};
      tasksData[taskId] = {
        id: taskId,
        ...task,
      };
    }
    this.Data.events?.emit('LOAD_TASKS_DATA', tasksList, RichTextParser.parseTasks(tasksData));
  }

  private handleTaskChanged(data: Editor.Data.Tasks.TaskData | null) {
    if (!data) {
      return;
    }
    this.Data.events?.emit('UPDATE_TASK', RichTextParser.parseTask(data));
  }

  stop(): void {}

  destroy(): void {
    this.tasksList?.destroy();
    this.deletedTasksList?.destroy();
  }

  getTaskModelById(taskId: string): Editor.Data.Task | undefined {
    return this.Data.models?.get('TASK', taskId);
  }

  async createTask(
    data: Editor.Data.Tasks.TaskDataLongProps,
    locations: string[],
  ): Promise<string> {
    return new Promise((resolve) => {
      const taskId = uuid();
      this.Data.transport.dispatchEvent(
        'TASK:CREATE',
        {
          task: {
            id: taskId,
            asg: data.assignee,
            u: this.Data.users?.loggedUserId as string,
            p_id: this.Data.structure?.structureData.parent_id as string,
            t: {
              c: new Date().toISOString(),
              d: data.dueDate ? new Date(data.dueDate).toISOString() : null,
            },
            s: Status.TO_DO,
            d: {
              dir: 'ltr',
              content: data.description,
              v: RichTextParser.getVersion,
            },
            w: [],
          },
          locations,
        },
        (response: RealtimeResponse<void>) => {
          if (response.success) {
            resolve(taskId);
          } else {
            reject(response.error);
          }
        },
      );
    });
  }

  async editTask(id: string, data: Partial<Editor.Data.Tasks.TaskDataLongProps>) {
    const editData: Editor.Data.Tasks.TaskEditData = {};
    if (data.dueDate) {
      editData.t = {
        d: new Date(data.dueDate).toISOString(),
      };
    } else {
      editData.t = {
        d: undefined,
      };
    }
    if (data.description) {
      editData.d = data.description;
    }
    if (data.assignee || data.assignee === null) {
      editData.asg = data.assignee;
    }
    return this.tasksList?.data[id].edit(editData);
  }

  async changeStatus(taskId: string, status: Editor.Data.Tasks.StatusValue) {
    return this.tasksList?.data[taskId].changeStatus(status);
  }

  async changeAssignee(taskId: string, user: string) {
    return this.tasksList?.data[taskId].changeAssignee(user);
  }

  async changeDescription(taskId: string, description: string) {
    return this.tasksList?.data[taskId].changeDescription(description);
  }

  async deleteTask(taskId: string) {
    return new Promise((resolve) => {
      this.Data.transport.dispatchEvent(
        'TASK:DELETE',
        {
          taskId,
        },
        (response: RealtimeResponse<void>) => {
          if (response.success) {
            resolve(taskId);
          } else {
            reject(response.error);
          }
        },
      );
    });
  }

  async removeTaskNode(nodeId: string) {
    const nodeTasks = this.Data.nodes?.getNodeTasks(nodeId);
    if (nodeTasks.length) {
      for (let index = 0; index < nodeTasks.length; index++) {
        const nodesWithTask = await this.tasksList?.getNodesWithTask(nodeTasks[index]);
        if (nodesWithTask?.length === 1 && nodesWithTask[0] === nodeId) {
          await this.tasksList?.data[nodeTasks[index]].deleteTask();
        }
      }
    }
  }

  async replyTask(id: string, replyContent: RichTextJSON) {
    return this.tasksList?.data[id].reply(
      {
        content: replyContent,
        dir: 'ltr',
        v: RichTextParser.getVersion,
      },
      this.Data.users?.loggedUserId as string,
    );
  }

  async editTaskReply(taskId: string, taskReplyId: string, content: RichTextJSON) {
    return this.tasksList?.data[taskId].editReply(taskReplyId, content);
  }

  async voteTaskReply(taskId: string, taskReplyId: string, vote: boolean) {
    return this.tasksList?.data[taskId].voteReply(
      taskReplyId,
      vote,
      this.Data.users?.loggedUserId as string,
    );
  }

  async deleteTaskReply(taskId: string, taskReplyId: string) {
    return this.tasksList?.data[taskId].deleteReply(taskReplyId);
  }

  async watchTask(taskId: string, userId: string = this.Data.users?.loggedUserId as string) {
    return this.tasksList?.data[taskId].watchTask(userId);
  }

  async removeWatchFromTask(taskId: string, user: string) {
    return this.tasksList?.data[taskId].removeWatchFromTask(user);
  }

  async reappendTask(taskId: string) {
    return this.tasksList?.data[taskId].reappend();
  }

  async getNodesWithTask(taskId: string) {
    await this.tasksList?.awaitForReady();
    return this.tasksList?.getNodesWithTask(taskId);
  }

  get list() {
    return this.tasksList;
  }

  get deleteList() {
    return this.deletedTasksList;
  }

  async applyOpsToTask(
    taskId: string,
    ops: Realtime.Core.RealtimeOps,
    options?: Realtime.Core.RealtimeSourceOptions,
  ) {
    const model = this.getTaskModelById(taskId);
    if (model?.loaded) {
      return model.apply(ops, options);
    }
  }

  async revertOpsToTask(
    taskId: string,
    ops: Realtime.Core.RealtimeOps,
    options?: Realtime.Core.RealtimeSourceOptions,
  ) {
    const model = this.getTaskModelById(taskId);
    if (model?.loaded) {
      return model.revert(ops, options);
    }
  }
}
